/**
 * Phase 30 — Create / Edit milestone dialog.
 *
 * Uses the existing Modal primitive with size="md". Mutations come from
 * useCreateMilestone / useUpdateMilestone (Plan 30-04). On success the modal
 * closes and the parent (KanbanBoardPage) shows a toast. On 409 (duplicate
 * name) the parent surfaces the inline error below the name field via the
 * mutation's `error` object.
 *
 * 260913-l8n / MKS-174 — status is no longer a free-form picker. Create mode
 * has no status control at all (new milestones always start stored 'active'
 * and read-time derive to 'planned' or 'active' from their start date).
 * Edit mode exposes a single "Mark as completed" toggle; when unchecked, the
 * derived status is shown as read-only text rather than editable.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useCreateMilestone, useUpdateMilestone } from '../../hooks/useMilestones';
import type { Milestone } from '../../services/api/milestones';
import { deriveMilestoneStatus, describeMilestoneStatus } from './milestoneStatus';
import { DateField } from '../ui/DateField';

export interface MilestoneFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  mode: 'create' | 'edit';
  milestone?: Milestone;
  onSuccess?: (verb: 'created' | 'updated') => void;
  onError?: (verb: 'created' | 'updated', message: string) => void;
}

export function MilestoneFormModal({
  isOpen,
  onClose,
  projectId,
  mode,
  milestone,
  onSuccess,
  onError,
}: MilestoneFormModalProps) {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const createMutation = useCreateMilestone(projectId);
  const updateMutation = useUpdateMilestone(projectId);

  // Hydrate / reset form when modal opens or target milestone changes.
  useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
      return;
    }
    if (mode === 'edit' && milestone) {
      setName(milestone.name);
      setStartDate(milestone.startDate ?? '');
      setEndDate(milestone.endDate ?? '');
      setIsCompleted(milestone.status === 'completed');
    } else {
      setName('');
      setStartDate('');
      setEndDate('');
      setIsCompleted(false);
    }
    setSubmitted(false);
  }, [isOpen, mode, milestone]);

  const trimmedName = name.trim();
  const nameError = submitted && trimmedName.length === 0 ? 'Name is required.' : null;
  const dateError = useMemo(() => {
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      return 'End date must be on or after start date.';
    }
    return null;
  }, [startDate, endDate]);

  // Live derived-status preview — recomputes on every startDate/isCompleted
  // change, which is the whole point of showing it live as the user types.
  const derivedStatus = useMemo(
    () => deriveMilestoneStatus(isCompleted, startDate || null),
    [isCompleted, startDate],
  );
  const derivedStatusLabel = useMemo(
    () => describeMilestoneStatus(derivedStatus, startDate || null),
    [derivedStatus, startDate],
  );

  const isValid = trimmedName.length > 0 && !dateError;
  const isPending = mode === 'create' ? createMutation.isPending : updateMutation.isPending;
  const serverError =
    mode === 'create'
      ? ((createMutation.error as Error | null)?.message ?? null)
      : ((updateMutation.error as Error | null)?.message ?? null);

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    setSubmitted(true);
    if (!isValid) return;
    const payload = {
      name: trimmedName,
      startDate: startDate || null,
      endDate: endDate || null,
      // Write side stays two-valued: create always starts stored 'active'
      // (its real-world status is derived from startDate on read); edit
      // sends 'completed' iff the toggle is checked, else 'active'.
      status: (mode === 'create'
        ? 'active'
        : isCompleted
          ? 'completed'
          : 'active') as 'active' | 'completed',
    };
    if (mode === 'create') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          onSuccess?.('created');
          onClose();
        },
        onError: (err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Unknown error';
          onError?.('created', msg);
        },
      });
    } else if (milestone) {
      updateMutation.mutate(
        { id: milestone.id, input: payload },
        {
          onSuccess: () => {
            onSuccess?.('updated');
            onClose();
          },
          onError: (err: unknown) => {
            const msg = err instanceof Error ? err.message : 'Unknown error';
            onError?.('updated', msg);
          },
        },
      );
    }
  }

  const inputClass =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'New milestone' : 'Edit milestone'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="ms-name">
            Name
          </label>
          <input
            id="ms-name"
            type="text"
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sprint 14, Q3 push, Beta launch"
            className={inputClass}
            autoFocus
          />
          {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="ms-start">
            Start date (optional)
          </label>
          <DateField
            id="ms-start"
            value={startDate}
            onChange={setStartDate}
            className={inputClass}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="ms-end">
            End date (optional)
          </label>
          <DateField
            id="ms-end"
            value={endDate}
            onChange={setEndDate}
            className={inputClass}
          />
          {dateError && <p className="mt-1 text-xs text-red-600">{dateError}</p>}
        </div>

        {mode === 'create' && (
          <p className="text-xs text-gray-600" data-testid="milestone-derived-status">
            Status: {derivedStatusLabel}
          </p>
        )}

        {mode === 'edit' && (
          <div>
            <div className="flex items-center gap-2">
              <input
                id="ms-completed"
                type="checkbox"
                checked={isCompleted}
                onChange={(e) => setIsCompleted(e.target.checked)}
              />
              <label className="text-xs font-medium text-gray-600" htmlFor="ms-completed">
                Mark as completed
              </label>
            </div>
            {!isCompleted && (
              <p className="mt-1 text-xs text-gray-600" data-testid="milestone-derived-status">
                Status: {derivedStatusLabel}
              </p>
            )}
          </div>
        )}

        {serverError && <p className="text-sm text-red-600">{serverError}</p>}

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            isLoading={isPending}
            disabled={!isValid || isPending}
          >
            {mode === 'create' ? 'Create milestone' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
